import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  TextField, 
  MenuItem, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow, 
  TableContainer, 
  Alert, 
  CircularProgress,
  Divider,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import BackupTableIcon from '@mui/icons-material/BackupTable';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DownloadIcon from '@mui/icons-material/Download';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import * as XLSX from 'xlsx';
import api from '../../api';

const AttendanceImport = () => {
  const [loading, setLoading] = useState(false);
  
  // Import State
  const [headers, setHeaders] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [mapping, setMapping] = useState({ emp_id: '', date: '', start_time: '', end_time: '', status: '' });
  const [importStatus, setImportStatus] = useState({ type: '', msg: '' });

  // Validation State
  const [validationResults, setValidationResults] = useState([]);
  const [allValid, setAllValid] = useState(false);
  
  // Show/hide instructions
  const [showInstructions, setShowInstructions] = useState(true);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setValidationResults([]);
    setAllValid(false);
    setImportStatus({ type: '', msg: '' });

    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: 'binary' });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const data = XLSX.utils.sheet_to_json(ws, { raw: false, defval: '' });
      
      if (data.length > 0) {
        setHeaders(Object.keys(data[0]));
        setExcelData(data);
        setImportStatus({ type: 'info', msg: `Loaded ${data.length} rows. Please map the columns below to validate.` });
      } else {
        setImportStatus({ type: 'error', msg: 'The uploaded Excel file is empty.' });
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleValidatePreview = async () => {
    if (!mapping.emp_id || !mapping.date || !mapping.start_time || !mapping.end_time || !mapping.status) {
      setImportStatus({ type: 'error', msg: 'Please map all required fields before validating.' });
      return;
    }

    setLoading(true);
    setImportStatus({ type: '', msg: '' });

    const rawRecords = excelData.map(row => {
      let rawDate = row[mapping.date] ? String(row[mapping.date]).trim() : '';
      rawDate = rawDate.replace(/\//g, '-');

      return {
        emp_id: row[mapping.emp_id] ? String(row[mapping.emp_id]).trim() : '',
        date: rawDate,
        start_time: row[mapping.start_time] ? String(row[mapping.start_time]).trim() : '',
        end_time: row[mapping.end_time] ? String(row[mapping.end_time]).trim() : '',
        status: row[mapping.status] ? String(row[mapping.status]).trim().toUpperCase() : ''
      };
    });

    try {
      const res = await api.post('/attendance-import/validate', rawRecords);
      const results = res.data;
      setValidationResults(results);
      
      const is100PercentValid = results.every(r => r.is_valid);
      setAllValid(is100PercentValid);
      
      if (is100PercentValid) {
        setImportStatus({ type: 'success', msg: 'Validation Passed! All records are clean and ready to import.' });
      } else {
        const errorCount = results.filter(r => !r.is_valid).length;
        setImportStatus({ type: 'error', msg: `Validation Failed. Found errors in ${errorCount} row(s). Please review the red highlights below, correct your Excel file, and re-upload.` });
      }
    } catch (err) {
      setImportStatus({ type: 'error', msg: err.response?.data?.detail || 'Validation request failed.' });
    } finally {
      setLoading(false);
    }
  };

  const calculateMinutes = (start, end) => {
    if (!start || !end) return 0;
    const [sH, sM] = start.split(':').map(Number);
    const [eH, eM] = end.split(':').map(Number);
    return ((eH * 60) + eM) - ((sH * 60) + sM);
  };

  const handleConfirmImport = async () => {
    if (!allValid) return;
    setLoading(true);

    const currentEmpId = localStorage.getItem('emp_id') || 'System';

    const finalRecords = validationResults.map(res => {
      const rec = res.record;
      return {
        emp_id: rec.emp_id,
        date: rec.date,
        first_login: `${rec.date} ${rec.start_time}`,
        last_logout: `${rec.date} ${rec.end_time}`,
        status: rec.status,
        total_office_minutes: calculateMinutes(rec.start_time, rec.end_time)
      };
    });

    try {
      const res = await api.post('/attendance-import/commit', {
        imported_by: currentEmpId,
        records: finalRecords
      });
      setImportStatus({ type: 'success', msg: `Successfully imported ${res.data.total_processed} records (${res.data.inserted_rows} New, ${res.data.updated_rows} Updated)!` });
      
      setExcelData([]);
      setValidationResults([]);
      setAllValid(false);
      setHeaders([]);
      setMapping({ emp_id: '', date: '', start_time: '', end_time: '', status: '' });
    } catch (err) {
      setImportStatus({ type: 'error', msg: err.response?.data?.detail || 'Failed to import records.' });
    } finally {
      setLoading(false);
    }
  };

  const downloadSampleExcel = () => {
    const sampleData = [
      {
        'Employee ID': 'TG10001',
        'Date': '2026-07-16',
        'Shift start time': '10:00:00',
        'Shift end time': '18:00:00',
        'Status': 'PRESENT'
      },
      {
        'Employee ID': 'TG10002',
        'Date': '2026-07-16',
        'Shift start time': '09:30:00',
        'Shift end time': '17:30:00',
        'Status': 'HALF-DAY'
      },
      {
        'Employee ID': 'TG10003',
        'Date': '2026-07-17',
        'Shift start time': '10:00:00',
        'Shift end time': '18:00:00',
        'Status': 'AL'
      },
      {
        'Employee ID': 'TG10004',
        'Date': '2026-07-17',
        'Shift start time': '10:00:00',
        'Shift end time': '18:00:00',
        'Status': 'PRESENT'
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance_Import");
    XLSX.writeFile(workbook, "Test_import_att.xlsx");
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}

      {/* Compact Instructions - Collapsible */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 1.5, 
          mb: 2, 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: '#f8f9fa'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon sx={{ color: '#1976d2', fontSize: 20 }} />
            <Typography variant="body2" fontWeight="medium">
              Format: Employee ID (TG10052), Date (YYYY-MM-DD), Shift start/end (HH:MM:SS), Status (PRESENT, HALF-DAY, LOP, WO, NH, AL, CL, ML, EL)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button 
              size="small" 
              variant="outlined" 
              startIcon={<DownloadIcon />}
              onClick={downloadSampleExcel}
              sx={{ fontSize: '0.75rem' }}
            >
              Sample Sheet
            </Button>
            <IconButton 
              size="small" 
              onClick={() => setShowInstructions(!showInstructions)}
            >
              {showInstructions ? <CloseIcon fontSize="small" /> : <InfoIcon fontSize="small" />}
            </IconButton>
          </Box>
        </Box>
        
        {showInstructions && (
          <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip label="Employee ID: TG10001" size="small" variant="outlined" />
            <Chip label="Date: 2026-07-16" size="small" variant="outlined" />
            <Chip label="Start: 10:00:00" size="small" variant="outlined" />
            <Chip label="End: 18:00:00" size="small" variant="outlined" />
            <Chip label="Status: PRESENT" size="small" color="primary" variant="outlined" />
            <Chip label="HALF-DAY" size="small" variant="outlined" />
            <Chip label="LOP" size="small" variant="outlined" />
            <Chip label="WO" size="small" variant="outlined" />
            <Chip label="NH" size="small" variant="outlined" />
            <Chip label="AL" size="small" variant="outlined" />
            <Chip label="CL" size="small" variant="outlined" />
            <Chip label="ML" size="small" variant="outlined" />
            <Chip label="EL" size="small" variant="outlined" />
          </Box>
        )}
      </Paper>

      {/* Main Import Function - Primary Focus */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, bgcolor: '#ffffff' }}>
        {importStatus.msg && (
          <Alert 
            severity={importStatus.type} 
            sx={{ mb: 3 }}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setImportStatus({ type: '', msg: '' })}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {importStatus.msg}
          </Alert>
        )}
        
        {/* File Upload - Primary Action */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          border: '2px dashed',
          borderColor: 'primary.main',
          borderRadius: 2,
          bgcolor: '#f0f7ff'
        }}>
          <UploadFileIcon sx={{ fontSize: 48, color: '#1976d2', mb: 2 }} />
          <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
            Select an Excel file to import attendance data
          </Typography>
          <Button 
            variant="contained" 
            component="label" 
            startIcon={<BackupTableIcon />} 
            disabled={loading}
            size="large"
          >
            Choose Excel File
            <input type="file" hidden accept=".xlsx, .xls, .csv" onChange={handleFileUpload} />
          </Button>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
            Supports .xlsx, .xls, .csv formats
          </Typography>
        </Box>

        {headers.length > 0 && excelData.length > 0 && validationResults.length === 0 && (
          <Box sx={{ mt: 4, p: 3, bgcolor: '#f9fafb', borderRadius: 2, border: '1px solid #eee' }}>
            <Typography variant="subtitle1" fontWeight="bold" mb={2}>Map Excel Columns to Database Fields</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
              {Object.keys(mapping).map(key => (
                <TextField 
                  key={key} select size="small" 
                  label={key.replace('_', ' ').toUpperCase()} 
                  value={mapping[key]} 
                  onChange={(e) => setMapping({ ...mapping, [key]: e.target.value })}
                  sx={{ minWidth: 180, bgcolor: '#fff' }}
                >
                  {headers.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                </TextField>
              ))}
            </Box>
            <Button variant="contained" color="primary" onClick={handleValidatePreview} disabled={loading}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Validate & Preview Data'}
            </Button>
          </Box>
        )}

        {validationResults.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">Data Preview & Validation</Typography>
              {allValid && (
                <Button variant="contained" color="success" onClick={handleConfirmImport} disabled={loading}>
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Confirm & Import to Database'}
                </Button>
              )}
            </Box>

            <TableContainer sx={{ maxHeight: 500, border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Emp ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Start Time</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>End Time</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Work Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Error Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {validationResults.map((row, idx) => (
                    <TableRow key={idx} sx={{ bgcolor: row.is_valid ? 'inherit' : '#ffebee' }}>
                      <TableCell>
                        {row.is_valid ? <CheckCircleIcon color="success" /> : <ErrorIcon color="error" />}
                      </TableCell>
                      <TableCell>{row.record.emp_id}</TableCell>
                      <TableCell>{row.record.date}</TableCell>
                      <TableCell>{row.record.start_time}</TableCell>
                      <TableCell>{row.record.end_time}</TableCell>
                      <TableCell>{row.record.status}</TableCell>
                      <TableCell sx={{ color: '#d32f2f', fontSize: '0.85rem' }}>
                        {!row.is_valid && row.errors.join(' | ')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AttendanceImport;